import React from 'react';
import { Link } from 'react-router-dom';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';
import { Check, Truck, AlertTriangle, MapPin } from 'lucide-react';

const MobileKitPage = () => {

  const packs = [
    {
      emoji: '🎒',
      name: 'Mobile Basic',
      price: 6500000,
      equipment: [
        '2 x 4K cameras on tripod',
        '2 microphones (wired or lapel)',
        '2 compact LED lights',
        'Dedicated technician included',
        'Delivery + installation + pickup',
        'Raw files delivered same evening'
      ]
    },
    {
      emoji: '📦',
      name: 'Mobile Standard',
      price: 8500000,
      popular: true,
      equipment: [
        '3 x 4K cameras (multi-angle)',
        '4 microphones (wired + lapel mix)',
        'Professional 3-point lighting kit',
        'Portable backdrop (black or white)',
        'Dedicated technician included',
        'Delivery + installation + pickup',
        'Raw files delivered same evening'
      ]
    },
    {
      emoji: '🏆',
      name: 'Mobile Pro',
      price: 14000000,
      equipment: [
        '3 x 4K cameras (multi-angle)',
        '4 microphones (full mix)',
        'Professional 3-point lighting kit',
        'Portable backdrop (color choice)',
        'Senior technician dedicated',
        'Delivery + installation + pickup',
        'Raw files organized and delivered same evening'
      ]
    }
  ];

  const includedAll = [
    'Dedicated technician at all times',
    'Professional delivery and installation',
    'Equipment pickup after session',
    'Raw files delivered same evening'
  ];

  const conditions = [
    { icon: '⚠️', text: 'Security deposit: Rp 10,000,000 (refunded on return of intact equipment)' },
    { icon: '📝', text: 'Service contract signed before departure' },
    { icon: '📍', text: 'Delivery radius: 15km max (beyond: +Rp 500,000 per 5km)' },
    { icon: '📅', text: 'Availability only when studio is not booked that day' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="MOBILE KIT"
        title="On-Location Equipment"
        description="Full mobile production kit delivered to your location in Bali with experienced technician."
        bookLink="/services/mobile-kit/book"
        bookLabel="Book Mobile Kit"
        heroImage="https://images.unsplash.com/photo-1764162685263-92ab9db5b47b?w=1600&q=80"
        metaTitle="Mobile Kit Rental | Canggu Podcast Studio"
        metaDescription="Mobile podcast and video equipment rental with technician in Bali. On-location professional production."
      />

      {/* Packs */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Mobile Packs</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {packs.map((pack, index) => (
              <div 
                key={index}
                className={`p-6 border ${
                  pack.popular 
                    ? 'border-[#F5A623] bg-[#F5A623]/5' 
                    : 'border-[#262626] bg-[#121212]'
                } relative`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 right-6 px-3 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                    POPULAR
                  </span>
                )}
                
                <div className="text-4xl mb-4">{pack.emoji}</div>
                <h3 className="text-2xl font-heading font-semibold text-white mb-2">{pack.name}</h3>
                <div className="text-3xl font-heading font-bold text-[#F5A623] mb-1">
                  Rp {(pack.price / 1000000).toFixed(1)}M
                </div>
                <p className="text-gray-500 text-sm mb-6">/day</p>
                
                <ul className="space-y-3">
                  {pack.equipment.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <Check className="w-4 h-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Included in All */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Included in All Packs</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {includedAll.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-[#121212] border border-[#262626]">
                <Check className="w-6 h-6 text-[#F5A623] flex-shrink-0" />
                <span className="text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mandatory Conditions */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Mandatory Conditions</h2>
          <div className="space-y-4">
            {conditions.map((condition, index) => (
              <div key={index} className="flex items-start gap-4 p-5 bg-[#121212] border border-amber-500/30">
                <span className="text-2xl">{condition.icon}</span>
                <span className="text-gray-300">{condition.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Need Equipment On-Location?
          </h2>
          <p className="text-gray-400 mb-8">
            Book our mobile kit with a dedicated technician for your off-site production.
          </p>
          <Link
            to="/services/mobile-kit/book"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors"
            data-testid="book-now-cta"
          >
            Book Mobile Kit
          </Link>
        </div>
      </section>

      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MobileKitPage;
